import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import styles from './Download.module.css';
import '../styles/mpd.css'; // Import common styles

export default function Download() {
  const FILES = {
    macZip: '/dist/MyPeopleDatabase-mac-1.0.zip',
    macAppleDmg: '/dist/MyPeopleDatabase-mac-apple-1.0.dmg',
    macIntelDmg: '/dist/MyPeopleDatabase-mac-intel-1.0.dmg',
    winZip: '/dist/MyPeopleDatabase-win-1.0.zip',
    winExe: '/dist/MyPeopleDatabase-1.0.0.exe',
  };

  const [published, setPublished] = useState({
    macZip: null,
    macAppleDmg: null,
    macIntelDmg: null,
    winZip: null,
    winExe: null,
  });

  const [macDmgRecommendation, setMacDmgRecommendation] = useState<null | 'apple' | 'intel'>(null);

  useEffect(() => {
    let cancelled = false;

    const formatDate = d =>
      d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
      });

    const fetchLastModified = async (key, url) => {
      try {
        const res = await fetch(url, { method: 'HEAD' });
        const lm = res.headers.get('last-modified');
        if (lm) {
          const date = formatDate(new Date(lm));
          if (!cancelled) setPublished(p => ({ ...p, [key]: date }));
        }
      } catch {
        // Ignore network errors; simply don't show a date
      }
    };

    Object.entries(FILES).forEach(([key, url]) => fetchLastModified(key, url));

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const detectMacDmg = async () => {
      try {
        // Best-effort only. Many browsers intentionally do not expose CPU architecture.
        // UA-CH can expose architecture in some Chromium-based browsers.
        // See: navigator.userAgentData (not supported everywhere).
        // If detection fails, we simply show guidance text instead.
        // @ts-expect-error userAgentData is not available in all TS lib dom versions
        const uad = navigator.userAgentData;
        if (!uad) return;

        const platform = (uad.platform || '').toLowerCase();
        if (!platform.includes('mac')) return;

        const entropy = await uad.getHighEntropyValues(['architecture']);
        const arch = (entropy?.architecture || '').toLowerCase();
        if (cancelled) return;

        if (arch.includes('arm')) setMacDmgRecommendation('apple');
        if (arch.includes('x86')) setMacDmgRecommendation('intel');
      } catch {
        // Ignore
      }
    };

    detectMacDmg();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Download MyPeopleDatabase Desktop App for Windows & macOS</title>
        <meta
          name="description"
          content="Download the MyPeopleDatabase desktop app for Windows and macOS. Get the latest installers, system requirements, and installation instructions to start organizing your contacts today."
        />
      </Helmet>
      <div className={styles.container}>
        <div className={styles.content}>
          <h1 className={styles.title}>Download MyPeopleDatabase Desktop</h1>

          <div className={styles.downloadInfo}>
            <h2>Ready to Get Started?</h2>
            <p>Choose the version that matches your operating system.</p>
            <p>
              Current version: <span className={styles.version}>1.0.0</span>
              {' • '}
              <Link to="/release-notes" className={styles.releaseNotesLink}>
                View Release Notes
              </Link>
            </p>
          </div>

          <div className={styles.downloadSection}>
            <h2>Choose Your Platform</h2>
            <div className={styles.platformSections}>
              <section className={styles.platformSection}>
                <h3>macOS</h3>
                <p>
                  Not sure which DMG is right for you?
                  <br />
                  Apple menu &gt; About This Mac:
                  <br />
                  If it says <b>Chip: Apple M1/M2/M3</b>, choose <b>Apple Silicon (DMG)</b>.
                  <br />
                  If it says <b>Processor: Intel</b>, choose <b>Intel (DMG)</b>.
                  {macDmgRecommendation === 'apple' ? (
                    <>
                      <br />
                      <b>Suggestion:</b> It looks like you're on an Apple Silicon Mac.
                    </>
                  ) : null}
                  {macDmgRecommendation === 'intel' ? (
                    <>
                      <br />
                      <b>Suggestion:</b> It looks like you're on an Intel Mac.
                    </>
                  ) : null}
                </p>
                <div className={styles.downloadGrid}>
                  <a
                    href={FILES.macAppleDmg}
                    className={`${styles.downloadButton} ${styles.macButton}`}
                    download="MyPeopleDatabase-mac-apple-1.0.dmg"
                  >
                    <span className={styles.platformIcon} role="img" aria-label="Apple">
                      🍎
                    </span>
                    <span>Apple Silicon (DMG)</span>
                    <span className={styles.fileInfo}>
                      DMG File
                      {published.macAppleDmg ? ` • Published on ${published.macAppleDmg}` : ''}
                    </span>
                  </a>
                  <a
                    href={FILES.macIntelDmg}
                    className={`${styles.downloadButton} ${styles.macButton}`}
                    download="MyPeopleDatabase-mac-intel-1.0.dmg"
                  >
                    <span className={styles.platformIcon} role="img" aria-label="Apple">
                      🍎
                    </span>
                    <span>Intel (DMG)</span>
                    <span className={styles.fileInfo}>
                      DMG File
                      {published.macIntelDmg ? ` • Published on ${published.macIntelDmg}` : ''}
                    </span>
                  </a>
                  {/*
                  <a
                    href={FILES.macZip}
                    className={`${styles.downloadButton} ${styles.macButton}`}
                    download="MyPeopleDatabase-mac-1.0.zip"
                  >
                    <span className={styles.platformIcon} role="img" aria-label="Apple">
                      🍎
                    </span>
                    <span>Intel (ZIP)</span>
                    <span className={styles.fileInfo}>
                      ZIP File{published.macZip ? ` • Published on ${published.macZip}` : ''}
                    </span>
                  </a>
                  */}
                </div>
              </section>

              <section className={styles.platformSection}>
                <h3>Windows</h3>
                <div className={styles.downloadGrid}>
                  <a
                    href={FILES.winExe}
                    className={`${styles.downloadButton} ${styles.windowsButton}`}
                    download="MyPeopleDatabase-1.0.0.exe"
                  >
                    <span className={styles.platformIcon} role="img" aria-label="Windows">
                      🪟
                    </span>
                    <span>Windows Installer (EXE)</span>
                    <span className={styles.fileInfo}>
                      ZIP File{published.winExe ? ` • Published on ${published.winExe}` : ''}
                    </span>
                  </a>
                </div>
              </section>

              <section className={styles.platformSection}>
                <h3>Windows</h3>
                <div className={styles.downloadGrid}>
                  <a
                    href={FILES.winZip}
                    className={`${styles.downloadButton} ${styles.windowsButton}`}
                    download="MyPeopleDatabase-win-1.0.zip"
                  >
                    <span className={styles.platformIcon} role="img" aria-label="Windows">
                      🪟
                    </span>
                    <span>Windows (ZIP)</span>
                    <span className={styles.fileInfo}>
                      ZIP File{published.winZip ? ` • Published on ${published.winZip}` : ''}
                    </span>
                  </a>
                </div>
              </section>
            </div>
          </div>

          <div className={styles.instructionsContainer}>
            <div className={styles.instructions}>
              <h3>Mac Installation</h3>
              <ol>
                <li>Open the downloaded dmg or zip file</li>
                <li>Drag MyPeopleDatabase folder to your Applications folder</li>
                <li>Find from your Applications folder</li>
                <li>Double click to launch</li>
              </ol>
            </div>

            <div className={styles.instructions}>
              <h3>Windows Installation</h3>
              <ol>
                <li>
                  Run the downloaded EXE installer <i>coming soon</i>
                </li>
                <li>Follow the installation wizard</li>
                <li>Launch from Start Menu or Desktop shortcut</li>
              </ol>
            </div>
          </div>

          <div className={styles.systemRequirements}>
            <h3>System Requirements</h3>
            <div className={styles.requirementsGrid}>
              <div>
                <h4>Mac</h4>
                <ul>
                  <li>macOS 12.0 or newer</li>
                  <li>2 GB RAM</li>
                  <li>500 MB free disk space</li>
                </ul>
              </div>
              <div>
                <h4>Windows</h4>
                <ul>
                  <li>Windows 10/11</li>
                  <li>2 GB RAM</li>
                  <li>500 MB free disk space</li>
                </ul>
              </div>
            </div>
          </div>

          <div className={styles.support}>
            <h3>Need Help?</h3>
            <p>
              If you encounter any issues with your download or installation, please contact our
              support team at{' '}
              <a href="mailto:support@mypeopledatabase.com">support@mypeopledatabase.com</a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
